// src/components/practice/PracticeEssayView.jsx
//
// GENERIC, reusable timed practice-essay activity (set-text / literature / skills
// mount the same one). Renders ONE essay_prompt item across three phases:
//   setup    -> show the prompt + Start
//   running  -> per-prompt timer ring + textarea + live word count + Submit
//   done     -> stats (word count + time used) + a SELF-ASSESSMENT reveal of the
//               item's sample essay side-by-side with the student's text + Retry
//
// L2b BOUNDARY: NO AI grade, NO AI feedback, NO "Submit for Tutor Review", NO
// L3/L4/L5 levels -- a single sample essay for self-comparison only (all of that
// is L3). If the prompt has no sampleEssay, the model-reveal is hidden gracefully
// (no dead "AI grade coming" placeholder). Faithful to the prototype
// LitTimedEssayView visual, minus the deferred AI/tutor pieces. In-session only.
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { IconClock, IconRefresh } from '../icons.jsx';
import { countWords, formatClock, parseEssayPrompt } from './practiceHelpers.js';
import { advanceEssayPageSession, createEssayPageSession, essayPageMinutes, essaySchoolOwnerMatches, restoreEssayPageSession } from './essayPageSession.js';
import { saveSchoolEssayAttempt } from '../../lib/callables.js';
import EssayFeedbackTiers from './EssayFeedbackTiers.jsx';
import { useEssayAttemptWriter } from '../../storage/useEssayAttempts.js';
import { auth } from '../../auth/firebase.js';
import { useAuth } from '../../auth/useAuth.js';

// Past-paper-style render of an essay prompt: NORMAL sentence case (font-serif, NOT
// the all-caps Cinzel display font) with paragraphs preserved -- the extract as an
// indented quote, the question stem as its own paragraph, and "Write about:" points
// as a list. Defensive: unrecognised formats fall back to plain paragraphs.
function EssayPromptBody({ prompt }) {
    const blocks = useMemo(() => parseEssayPrompt(prompt), [prompt]);
    if (!blocks.length) return null;
    return (
        <div className="space-y-3 text-left">
            {blocks.map((b, i) => {
                if (b.kind === 'extract') {
                    return <p key={i} className="font-serif italic text-book-text/90 text-base leading-relaxed border-l-2 border-book-accent/40 pl-4 whitespace-pre-line">{b.text}</p>;
                }
                if (b.kind === 'question') {
                    return <p key={i} className="font-serif text-book-text text-base font-semibold leading-relaxed whitespace-pre-line">{b.text}</p>;
                }
                if (b.kind === 'instruction') {
                    return <p key={i} className="font-display text-book-accent text-xs font-semibold">{b.text}</p>;
                }
                if (b.kind === 'list') {
                    return (
                        <ul key={i} className="list-disc pl-6 space-y-1 font-serif text-book-text text-base leading-relaxed">
                            {b.items.map((it, j) => <li key={j}>{it}</li>)}
                        </ul>
                    );
                }
                return <p key={i} className="font-serif text-book-text text-base leading-relaxed whitespace-pre-line">{b.text}</p>;
            })}
        </div>
    );
}

// `packId` + `entitled` drive the AI feedback tier (L3); `tutorFeeNote` is optional
// copy for the Tutor Review panel. All optional -> the component still runs as a
// pure timed-essay activity if a host omits them.
export default function PracticeEssayView(props) {
    const { currentUser } = useAuth();
    const ownerUid = currentUser?.uid || null;
    const minutes = essayPageMinutes(props.item, props.minutesOverride);
    // A prompt/pack/duration boundary resets all transient refs and feedback. The
    // optional reader-owned snapshot restores only a matching in-page attempt.
    const sessionKey = JSON.stringify([ownerUid, props.packId ?? null, props.item?.id ?? null, minutes]);
    return <PracticeEssayActivity key={sessionKey} {...props} ownerUid={ownerUid} minutes={minutes} />;
}

function PracticeEssayActivity({ ownerUid, item, packId, entitled = false, tutorFeeNote, minutes, pageSession, onPageSessionChange, onPhaseChange }) {
    const content = (item && item.content) || {};
    const prompt = content.prompt || 'Practice essay question.';
    const sampleEssay = typeof content.sampleEssay === 'string' && content.sampleEssay.trim()
        ? content.sampleEssay : null;
    const title = (item && item.title) || 'Timed Essay';
    const totalSeconds = minutes * 60;
    const promptId = item && item.id;
    const identity = { ownerUid, packId: packId ?? null, promptId: promptId ?? null, minutes };
    const sessionEnabled = typeof onPageSessionChange === 'function' || !!pageSession;
    // pageSession is an initial snapshot, not a controlled prop. In-place typing
    // cannot be overwritten by a parent rendering the last published snapshot.
    const [session, setSession] = useState(() => restoreEssayPageSession(pageSession, identity) || createEssayPageSession(identity));
    const sessionRef = useRef(session);
    const callbackRef = useRef({ onPageSessionChange, onPhaseChange });
    const mounted = useRef(false);
    const schoolSave = useRef(session.schoolSubmission?.promise || null);
    const { phase, answer, remaining } = session;
    const saveMessage = session.schoolSubmission?.message || '';
    const [showModel, setShowModel] = useState(false);
    const { saveDraft, canSaveDraft } = useEssayAttemptWriter();
    const [draftMessage, setDraftMessage] = useState('');
    const [savingDraft, setSavingDraft] = useState(false);

    useEffect(() => { callbackRef.current = { onPageSessionChange, onPhaseChange }; }, [onPageSessionChange, onPhaseChange]);
    const updateSession = useCallback((patch) => {
        const next = { ...sessionRef.current, ...patch };
        sessionRef.current = next;
        setSession(next);
        // Publish synchronously with edits/navigation events. This is page memory
        // only, never an automatic cloud draft or localStorage persistence.
        callbackRef.current.onPageSessionChange?.(next);
    }, []);
    useEffect(() => {
        mounted.current = true;
        callbackRef.current.onPageSessionChange?.(sessionRef.current);
        return () => { mounted.current = false; };
    }, []);
    useEffect(() => { callbackRef.current.onPhaseChange?.(phase); }, [phase]);
    useEffect(() => {
        // A school save started by the previous mount can finish after this mount
        // has resumed. Observe its shared handle without sending another request.
        const handle = sessionRef.current.schoolSubmission;
        if (!handle) return undefined;
        let active = true;
        const refresh = () => {
            if (active && sessionRef.current.schoolSubmission === handle
                && essaySchoolOwnerMatches(sessionRef.current.ownerUid, auth?.currentUser?.uid)) updateSession({ schoolSubmission: handle });
        };
        Promise.resolve(handle.promise).then(refresh, refresh);
        return () => { active = false; };
    }, [updateSession]);

    const saveSchool = useCallback(() => {
        const attempt = sessionRef.current;
        if (!attempt.answer.trim() || !packId || !essaySchoolOwnerMatches(attempt.ownerUid, auth?.currentUser?.uid)) return;
        // The same handle survives temporary unmounts. Its Promise is also exposed
        // through the existing ref consumed by EssayFeedbackTiers. Settling it
        // updates shared memory but never republishes into an unmounted owner.
        const handle = { promise: null, status: 'pending', message: '' };
        const settle = (status, message) => {
            handle.status = status; handle.message = message;
            if (mounted.current && sessionRef.current.schoolSubmission === handle
                && essaySchoolOwnerMatches(attempt.ownerUid, auth?.currentUser?.uid)) updateSession({ schoolSubmission: handle });
        };
        handle.promise = Promise.resolve().then(() => {
            // Auth can change after scheduling but before this microtask runs.
            // Check the attempt's captured owner immediately before dispatch.
            if (!essaySchoolOwnerMatches(attempt.ownerUid, auth?.currentUser?.uid)) return null;
            return saveSchoolEssayAttempt({
                attemptId: attempt.schoolAttemptId, packId, promptId, essayText: attempt.answer,
                elapsedSeconds: totalSeconds - attempt.remaining,
            });
        }).then(result => {
            const id = result?.id || null;
            settle(id ? 'saved' : 'skipped', id ? 'Saved to your school learning report. Your teachers can review this essay.' : '');
            return id;
        }).catch(() => {
            settle('error', 'School submission could not be saved. Keep this page open and retry.');
            return null;
        });
        schoolSave.current = handle.promise;
        updateSession({ schoolSubmission: handle });
    }, [packId, promptId, totalSeconds, updateSession]);

    const saveCurrentDraft = async () => {
        if (savingDraft) return;
        const attempt = sessionRef.current;
        const isCurrent = () => mounted.current && sessionRef.current.schoolAttemptId === attempt.schoolAttemptId
            && essaySchoolOwnerMatches(attempt.ownerUid, auth?.currentUser?.uid);
        setSavingDraft(true); setDraftMessage('');
        try {
            await saveDraft({ packId, promptId: item.id, promptTitle: title, essayText: attempt.answer });
            if (isCurrent()) setDraftMessage('Draft snapshot saved in Account → My Essays. Save again after editing.');
        } catch {
            if (isCurrent()) setDraftMessage('Draft save could not be confirmed. Keep this page open and copy your answer somewhere safe.');
        } finally { if (isCurrent()) setSavingDraft(false); }
    };
    const startEssay = () => {
        schoolSave.current = null;
        setShowModel(false); setDraftMessage(''); setSavingDraft(false);
        updateSession({
            phase: 'running', answer: '', remaining: totalSeconds,
            deadlineAt: Date.now() + totalSeconds * 1000,
            schoolAttemptId: crypto.randomUUID(), schoolSubmission: null,
        });
    };
    const submitEssay = () => {
        const current = sessionRef.current;
        const latest = sessionEnabled ? advanceEssayPageSession(current, Date.now()) : current;
        updateSession({ phase: 'done', remaining: latest.remaining });
    };

    // Keep warning: an acknowledged snapshot may not contain the latest edits.
    useEffect(() => {
        if (phase === 'setup' || answer.trim().length === 0) return undefined;
        const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [phase, answer]);

    // Retained sessions keep their original deadline even while the reader shows
    // another page. Hosts without the optional seam retain their existing ticking.
    useEffect(() => {
        if (phase !== 'running') return undefined;
        const tick = () => {
            const current = sessionRef.current;
            if (current.phase !== 'running') return;
            const remaining = sessionEnabled
                ? advanceEssayPageSession(current, Date.now()).remaining
                : Math.max(0, current.remaining - 1);
            if (remaining !== current.remaining) updateSession({ remaining, phase: remaining > 0 ? 'running' : 'done' });
        };
        if (sessionEnabled) tick();
        const t = setInterval(tick, 1000);
        return () => clearInterval(t);
    }, [phase, sessionEnabled, updateSession]);

    useEffect(() => { if (phase === 'done' && !schoolSave.current) saveSchool(); }, [phase, saveSchool]);
    const wordCount = countWords(answer);

    if (phase === 'setup') {
        return (
            <div className="max-w-3xl mx-auto pb-12">
                <div className="mb-6 pb-4 border-b-2 border-book-accent/30">
                    <div className="font-display text-book-accent text-xs mb-2 font-semibold">Practice · Timed Essay</div>
                    <h2 className="font-display text-2xl md:text-4xl text-book-accent leading-tight">{title}</h2>
                </div>
                <div className="border border-book-accent/30 bg-book-page/40 p-8 text-center text-book-accent">
                    <IconClock className="w-12 h-12 mx-auto mb-4" />
                    <div className="font-display text-book-muted text-xs mb-2 font-semibold">Today's question</div>
                    <div className="max-w-xl mx-auto mb-4"><EssayPromptBody prompt={prompt} /></div>
                    <p className="font-serif italic text-book-muted mb-6">
                        {minutes} minutes. The timer runs until you submit or time expires.
                        {' At the end you can get instant AI feedback on your response'
                            + (sampleEssay ? ', or compare with a model answer,' : '') + ' to self-assess.'}
                    </p>
                    <button onClick={startEssay}
                        className="px-5 py-3 bg-book-accent text-black font-display text-sm hover:bg-book-accentLight transition-colors font-semibold">
                        Start {minutes}-minute essay
                    </button>
                </div>
            </div>
        );
    }

    if (phase === 'running') {
        const r = 80; const circ = 2 * Math.PI * r;
        const dash = ((totalSeconds - remaining) / totalSeconds) * circ;
        return (
            <div className="max-w-4xl mx-auto pb-12">
                <div className="sticky top-2 z-20 flex justify-center mb-6">
                    <div className="relative w-40 h-40">
                        <svg width="160" height="160">
                            <circle cx="80" cy="80" r={r} fill="none" stroke="rgba(201,169,97,0.2)" strokeWidth="6" />
                            <circle cx="80" cy="80" r={r} fill="none" stroke="currentColor" className="text-book-accent" strokeWidth="6"
                                strokeDasharray={circ} strokeDashoffset={circ - dash} strokeLinecap="round" />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <div className="font-display text-book-accent text-4xl">{formatClock(remaining)}</div>
                            <div className="font-serif italic text-book-muted text-xs">remaining</div>
                        </div>
                    </div>
                </div>
                <div className="border-2 border-book-accent/50 bg-book-page/40 p-5 mb-5">
                    <div className="font-display text-book-muted text-xs mb-2 font-semibold">Essay question</div>
                    <EssayPromptBody prompt={prompt} />
                </div>
                <textarea value={answer} onChange={(e) => updateSession({ answer: e.target.value })} rows={22} placeholder="Write your essay here..."
                    className="selectable w-full bg-book-bg border border-book-accent/30 p-5 text-book-text font-serif text-base leading-relaxed focus:outline-none focus:border-book-accent resize-y" />
                <div className="flex items-center justify-between mt-4 flex-wrap gap-3">
                    <div className="font-serif italic text-book-muted text-sm">{wordCount} words</div>
                    {canSaveDraft && <button type="button" onClick={saveCurrentDraft} disabled={savingDraft || !answer.trim()}
                        className="px-4 py-2 border border-book-accent/40 text-book-accent text-sm disabled:opacity-40">
                        {savingDraft ? 'Saving draft…' : 'Save draft without AI'}
                    </button>}
                    <button onClick={submitEssay}
                        className="px-5 py-2.5 bg-book-accent text-black font-display text-xs hover:bg-book-accentLight transition-colors font-semibold">
                        Submit essay
                    </button>
                </div>
                {draftMessage && <p role="status" className="mt-2 text-book-muted text-xs">{draftMessage}</p>}
            </div>
        );
    }

    // done
    return (
        <div className="max-w-4xl mx-auto pb-12">
            <div className="mb-6 pb-4 border-b-2 border-book-accent/30">
                <div className="font-display text-book-accent text-xs mb-2 font-semibold">Results</div>
                <h2 className="font-display text-2xl md:text-4xl text-book-accent leading-tight">Essay submitted</h2>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-6 max-w-md">
                <div className="border border-book-accent/20 bg-book-page/40 p-5 text-book-accent">
                    <div className="font-display text-book-muted text-xs mb-2 font-semibold">Word count</div>
                    <div className="font-display text-book-accent text-2xl">{wordCount}</div>
                </div>
                <div className="border border-book-accent/20 bg-book-page/40 p-5 text-book-accent">
                    <div className="font-display text-book-muted text-xs mb-2 font-semibold">Time used</div>
                    <div className="font-display text-book-accent text-2xl">{formatClock(totalSeconds - remaining)}</div>
                </div>
            </div>

            {saveMessage && <p role="status" className="my-3 text-sm">{saveMessage}{saveMessage.includes('could not') && <button className="underline ml-2" onClick={saveSchool}>Retry saving</button>}</p>}
            {/* L3: the two feedback tiers (AI instant + Tutor Review quote-request). */}
            <EssayFeedbackTiers
                packId={packId}
                promptId={item && item.id}
                promptTitle={item && item.title}
                essayText={answer}
                entitled={entitled}
                tutorFeeNote={tutorFeeNote} schoolSave={schoolSave} />

            {sampleEssay && (
                <div className="mt-8 mb-5 border-t border-book-accent/20 pt-6">
                    <button onClick={() => setShowModel((v) => !v)}
                        className="px-4 py-2 border border-book-accent/50 text-book-accent font-display text-sm hover:bg-book-accent/10 transition-colors font-semibold">
                        {showModel ? 'Hide' : 'Show'} model essay
                    </button>
                    {showModel && (
                        <div className="grid md:grid-cols-2 gap-3 mt-4">
                            <div className="border border-book-accent/20 bg-book-page/40 p-5">
                                <div className="font-display text-book-muted text-xs mb-2 font-semibold">Your essay</div>
                                <p className="font-serif text-book-text leading-relaxed text-sm whitespace-pre-wrap">
                                    {answer.trim() ? answer : <span className="italic opacity-60">— no answer submitted —</span>}
                                </p>
                            </div>
                            <div className="border border-book-accent/40 bg-book-accent/5 p-5">
                                <div className="font-display text-book-accent text-xs mb-2 font-semibold">Model answer · for self-assessment</div>
                                <p className="font-serif text-book-text leading-relaxed text-sm whitespace-pre-wrap">{sampleEssay}</p>
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className="mt-2">
                <button onClick={() => { updateSession({ phase: 'setup', deadlineAt: null }); setShowModel(false); }}
                    className="inline-flex items-center gap-2 px-5 py-2 bg-book-accent text-black font-display text-xs hover:bg-book-accentLight transition-colors font-semibold">
                    <IconRefresh className="w-4 h-4" /> Retry
                </button>
            </div>
        </div>
    );
}
